// Dr.Rx™ Clinical Pearls Editorial Studio
import React, { useState, useEffect } from 'react';
import {
    getPearlQueue,
    savePearl,
    publishPearl,
    getEditorialConfig,
    setEditorialConfig,
    forceGenerateDraft,
    type ClinicalPearl
} from '../../services/pearlService';
import { scanClinicalNews, type NewsTopic } from '../../services/adminService';
import { useToast } from '../../contexts/ToastContext';

const SparklesIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
);
const GlobeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
);

const EditorialStudio: React.FC = () => {
    const { showToast } = useToast();
    const [queue, setQueue] = useState<ClinicalPearl[]>([]);
    const [selectedPearl, setSelectedPearl] = useState<ClinicalPearl | null>(null);
    const [autoMode, setAutoMode] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // News Intelligence Scanner Modal
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [isScanning, setIsScanning] = useState(false);
    const [newsTopics, setNewsTopics] = useState<NewsTopic[]>([]);

    useEffect(() => {
        const init = async () => {
            try {
                const [q, cfg] = await Promise.all([getPearlQueue(), getEditorialConfig()]);
                setQueue(q);

                const todayStr = new Date().toISOString().split('T')[0];
                const todayPearl = q.find(p => p.scheduledDate === todayStr);
                if (todayPearl) {
                    setSelectedPearl(todayPearl);
                } else {
                    setSelectedPearl({
                        id: todayStr,
                        topic: '',
                        content: '',
                        category: 'General',
                        source: '',
                        sourceUrl: '',
                        status: 'DRAFT',
                        scheduledDate: todayStr
                    });
                }

                setAutoMode(cfg.autoMode);
            } catch (e) {
                console.error('Editorial Studio initialization error:', e);
            } finally {
                setIsLoading(false);
            }
        };
        init();
    }, []);

    const handleAutoModeToggle = async () => {
        const newState = !autoMode;
        setAutoMode(newState);
        await setEditorialConfig(newState);
        showToast(`Editorial Auto-Pilot ${newState ? 'activated' : 'paused'}.`, 'info');
    };

    const handleGenerate = async (topic?: string) => {
        if (!selectedPearl) return;
        setIsSaving(true);
        setIsScannerOpen(false);
        try {
            const newDraft = await forceGenerateDraft(topic);
            const pearlWithMeta: ClinicalPearl = {
                ...newDraft,
                id: selectedPearl.scheduledDate || 'draft',
                status: 'DRAFT',
                scheduledDate: selectedPearl.scheduledDate,
                sourceUrl: newDraft.sourceUrl || ''
            };
            setSelectedPearl(pearlWithMeta);

            const newQueue = queue.filter(p => p.scheduledDate !== pearlWithMeta.scheduledDate);
            setQueue([...newQueue, pearlWithMeta].sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate)));
            showToast('AI Clinical Pearl draft generated.', 'success');
        } catch (e: any) {
            showToast(`AI draft generation failed: ${e.message || 'Unknown error'}`, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleScanNews = async () => {
        setIsScanning(true);
        try {
            const topics = await scanClinicalNews();
            setNewsTopics(topics);
        } catch (e: any) {
            showToast('Failed to scan medical news sources.', 'error');
        } finally {
            setIsScanning(false);
        }
    };

    const handleSaveCurrent = async (publishNow = false) => {
        if (!selectedPearl || !selectedPearl.topic.trim()) {
            showToast('Please enter a headline before saving.', 'warning');
            return;
        }
        setIsSaving(true);
        try {
            const updatedPearl: ClinicalPearl = {
                ...selectedPearl,
                status: publishNow ? 'PUBLISHED' : selectedPearl.status
            };
            await savePearl(updatedPearl);
            if (updatedPearl.status === 'PUBLISHED') {
                await publishPearl(updatedPearl);
            }
            const q = await getPearlQueue();
            setQueue(q);
            setSelectedPearl(updatedPearl);
            showToast(
                updatedPearl.status === 'PUBLISHED'
                    ? `Clinical Pearl published for ${updatedPearl.scheduledDate}.`
                    : 'Draft saved to schedule.',
                'success'
            );
        } catch (e: any) {
            showToast(`Failed to save Clinical Pearl: ${e.message}`, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const getWeekDays = () => {
        const days = [];
        const today = new Date();
        for (let i = 0; i < 7; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() + i);
            days.push(d.toISOString().split('T')[0]);
        }
        return days;
    };

    const handleDayClick = (dateStr: string) => {
        const existing = queue.find(p => p.scheduledDate === dateStr);
        if (existing) {
            setSelectedPearl(existing);
        } else {
            setSelectedPearl({
                id: dateStr,
                topic: '',
                content: '',
                category: 'General',
                source: '',
                sourceUrl: '',
                status: 'DRAFT',
                scheduledDate: dateStr
            });
        }
    };

    if (isLoading) return <div className="text-center py-20 text-gray-400">Loading Editorial Studio...</div>;

    return (
        <div className="flex flex-col lg:flex-row gap-6 min-h-[700px] relative">
            {/* Medical News Intelligence Scanner Modal */}
            {isScannerOpen && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#15243E] border border-blue-900/60 w-full max-w-2xl rounded-2xl p-6 flex flex-col max-h-[80vh] shadow-2xl">
                        <div className="flex justify-between items-center mb-4 pb-3 border-b border-blue-900/50">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                <GlobeIcon /> Medical News & Guideline Intelligence
                            </h3>
                            <button onClick={() => setIsScannerOpen(false)} className="text-gray-400 hover:text-white p-1">
                                ✕
                            </button>
                        </div>

                        <div className="flex-grow overflow-y-auto custom-scrollbar space-y-3 pr-1">
                            {isScanning ? (
                                <div className="text-center py-20 text-blue-300 animate-pulse">
                                    Scanning FDA approvals, guideline updates, and clinical literature...
                                </div>
                            ) : newsTopics.length > 0 ? (
                                newsTopics.map((news, i) => (
                                    <div
                                        key={i}
                                        className="bg-black/20 p-4 rounded-xl border border-blue-900/40 hover:border-yellow-400/50 transition-all cursor-pointer group"
                                        onClick={() => handleGenerate(news.headline)}
                                    >
                                        <div className="flex justify-between items-start gap-2 mb-2">
                                            <h4 className="font-bold text-white group-hover:text-yellow-300 transition-colors">
                                                {news.headline}
                                            </h4>
                                            <span className="text-[10px] bg-blue-900/50 px-2 py-1 rounded text-blue-200 whitespace-nowrap">
                                                {news.source}
                                            </span>
                                        </div>
                                        <p className="text-xs text-gray-400 mb-3 leading-relaxed">{news.summary}</p>
                                        <button className="w-full py-2 bg-blue-600/20 hover:bg-blue-600 text-blue-300 hover:text-white rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-2">
                                            <SparklesIcon /> Draft Clinical Pearl From Topic
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-16 text-gray-400">
                                    <p>Click below to scan recent FDA and clinical news topics.</p>
                                    <button
                                        onClick={handleScanNews}
                                        className="mt-4 bg-yellow-400 text-gray-950 font-bold px-6 py-2 rounded-xl"
                                    >
                                        Scan Live Sources
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Left Column: Auto-Pilot & 7-Day Schedule */}
            <div className="w-full lg:w-1/3 flex flex-col gap-6">
                <div className="bg-gradient-to-br from-purple-900/40 to-[#1E2E4A] p-5 rounded-2xl border border-purple-500/30 shadow-lg">
                    <div className="flex justify-between items-center">
                        <div>
                            <h3 className="text-base font-bold text-white flex items-center gap-2">
                                <SparklesIcon /> Daily Pearl Auto-Pilot
                            </h3>
                            <p className="text-xs text-purple-200 mt-1">
                                Status: <span className="font-bold">{autoMode ? 'ACTIVE (6:00 AM EST)' : 'PAUSED'}</span>
                            </p>
                        </div>
                        <button
                            onClick={handleAutoModeToggle}
                            className={`w-12 h-7 rounded-full transition-colors relative ${
                                autoMode ? 'bg-emerald-500' : 'bg-gray-600'
                            }`}
                            aria-label="Toggle Daily Pearl Auto-Pilot"
                        >
                            <div
                                className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${
                                    autoMode ? 'left-6' : 'left-1'
                                }`}
                            />
                        </button>
                    </div>
                </div>

                {/* 7-Day Content Schedule */}
                <div className="bg-[#1E2E4A] border border-blue-900/50 rounded-2xl flex-grow flex flex-col overflow-hidden">
                    <div className="p-4 border-b border-blue-900/50 flex justify-between items-center bg-[#15243E]">
                        <h3 className="font-bold text-white text-sm">7-Day Publication Queue</h3>
                        <button
                            onClick={() => {
                                setIsScannerOpen(true);
                                if (newsTopics.length === 0) handleScanNews();
                            }}
                            className="text-xs bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-3 py-1.5 rounded-xl flex items-center gap-1.5 shadow-md transition-colors"
                        >
                            <GlobeIcon /> Scan News
                        </button>
                    </div>
                    <div className="overflow-y-auto flex-grow p-3 space-y-2 custom-scrollbar">
                        {getWeekDays().map((day, index) => {
                            const pearlForDay = queue.find(p => p.scheduledDate === day);
                            const isSelected = selectedPearl?.scheduledDate === day;
                            const status = pearlForDay?.status || 'EMPTY';

                            return (
                                <button
                                    key={day}
                                    onClick={() => handleDayClick(day)}
                                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                                        isSelected
                                            ? 'bg-blue-600/20 border-yellow-400/60 text-white shadow-md'
                                            : 'bg-black/20 border-transparent text-gray-400 hover:bg-white/5'
                                    }`}
                                >
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-xs font-mono font-bold text-blue-300 tabular-nums">
                                            {index === 0 ? 'TODAY' : day}
                                        </span>
                                        <span
                                            className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                                                status === 'PUBLISHED'
                                                    ? 'bg-emerald-900/50 text-emerald-400 border border-emerald-500/30'
                                                    : status === 'DRAFT'
                                                    ? 'bg-yellow-900/50 text-yellow-400 border border-yellow-500/30'
                                                    : 'bg-gray-800 text-gray-500'
                                            }`}
                                        >
                                            {status}
                                        </span>
                                    </div>
                                    <p className="font-semibold text-sm line-clamp-1 text-white">
                                        {pearlForDay?.topic || 'Empty Slot — Click to Draft'}
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Right Column: Clinical Pearl Editor */}
            <div className="w-full lg:w-2/3 bg-[#1E2E4A] border border-blue-900/50 rounded-2xl flex flex-col overflow-hidden">
                <div className="p-4 border-b border-blue-900/50 bg-[#15243E] flex flex-wrap justify-between items-center gap-3">
                    <div>
                        <h3 className="font-bold text-white">Clinical Pearl Editor</h3>
                        <p className="text-xs text-gray-400 font-mono tabular-nums">
                            {selectedPearl ? `Scheduled Date: ${selectedPearl.scheduledDate}` : 'Select a date'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handleGenerate()}
                            disabled={isSaving || !selectedPearl}
                            className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3.5 py-2 rounded-xl font-bold flex items-center gap-1.5 transition-colors disabled:opacity-50"
                        >
                            <SparklesIcon /> AI Draft
                        </button>
                        <button
                            onClick={() => handleSaveCurrent(false)}
                            disabled={isSaving || !selectedPearl}
                            className="text-xs bg-[#101D35] hover:bg-blue-900/60 text-gray-200 border border-blue-800/60 px-3.5 py-2 rounded-xl font-bold transition-colors disabled:opacity-50"
                        >
                            Save Draft
                        </button>
                        <button
                            onClick={() => handleSaveCurrent(true)}
                            disabled={isSaving || !selectedPearl}
                            className="text-xs bg-yellow-400 hover:bg-yellow-300 text-gray-950 px-4 py-2 rounded-xl font-bold shadow-lg transition-colors disabled:opacity-50"
                        >
                            {isSaving ? 'Saving...' : 'Save & Publish'}
                        </button>
                    </div>
                </div>

                {selectedPearl ? (
                    <div className="p-6 overflow-y-auto space-y-5 custom-scrollbar flex-grow">
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                                Clinical Headline / Topic
                            </label>
                            <input
                                type="text"
                                value={selectedPearl.topic}
                                onChange={e => setSelectedPearl({ ...selectedPearl, topic: e.target.value })}
                                className="w-full bg-black/25 border border-blue-900/60 rounded-xl p-3 text-white font-bold text-base focus:border-yellow-400 outline-none"
                                placeholder="e.g., SGLT2 Inhibitors in Acute Heart Failure Decompensation..."
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                                    Clinical Category
                                </label>
                                <select
                                    value={selectedPearl.category}
                                    onChange={e => setSelectedPearl({ ...selectedPearl, category: e.target.value as any })}
                                    className="w-full bg-black/25 border border-blue-900/60 rounded-xl p-3 text-white text-sm"
                                >
                                    <option>Pharmacology</option>
                                    <option>Clinical Guideline</option>
                                    <option>Law</option>
                                    <option>Calculation</option>
                                    <option>General</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                                    Publish Status
                                </label>
                                <select
                                    value={selectedPearl.status}
                                    onChange={e => setSelectedPearl({ ...selectedPearl, status: e.target.value as any })}
                                    className="w-full bg-black/25 border border-blue-900/60 rounded-xl p-3 text-white text-sm font-bold"
                                >
                                    <option value="DRAFT">DRAFT</option>
                                    <option value="PUBLISHED">PUBLISHED (LIVE)</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                                    Citation / Guideline Source
                                </label>
                                <input
                                    type="text"
                                    value={selectedPearl.source || ''}
                                    onChange={e => setSelectedPearl({ ...selectedPearl, source: e.target.value })}
                                    className="w-full bg-black/25 border border-blue-900/60 rounded-xl p-3 text-white text-sm"
                                    placeholder="e.g., NEJM 2025 / ACC-AHA Guidelines"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                                    Source URL (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={selectedPearl.sourceUrl || ''}
                                    onChange={e => setSelectedPearl({ ...selectedPearl, sourceUrl: e.target.value })}
                                    className="w-full bg-black/25 border border-blue-900/60 rounded-xl p-3 text-blue-300 text-sm"
                                    placeholder="https://..."
                                />
                            </div>
                        </div>

                        <div className="flex-grow flex flex-col">
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                                Clinical Pearl Content
                            </label>
                            <textarea
                                value={selectedPearl.content}
                                onChange={e => setSelectedPearl({ ...selectedPearl, content: e.target.value })}
                                className="w-full bg-black/25 border border-blue-900/60 rounded-xl p-4 text-gray-200 text-sm leading-relaxed h-56 resize-none focus:border-yellow-400 outline-none"
                                placeholder="Write a concise, high-yield clinical pearl (2-3 sentences)..."
                            />
                        </div>
                    </div>
                ) : (
                    <div className="flex-grow flex flex-col items-center justify-center text-gray-500 p-8">
                        <p>Select a date from the 7-Day Publication Queue to edit.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditorialStudio;
